import { useState } from "react";
import axios from "axios";
import FileSaver from "file-saver";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/core/basic/Button";
import { getURLPrefix } from "../../utils/getUrlPrefix";
import * as Sentry from "@sentry/react";
import { isNetworkError } from "../../utils/isNetworkError";
import { errorToast } from "../../utils/toasts";
import { FaRegFileExcel } from "react-icons/fa6";
import { type UseMutationResult } from "@tanstack/react-query";

const URL_PREFIX = getURLPrefix();

interface Props {
  url_path: string;
  filters: Record<string, unknown>;
  /**
   * This param is probably deprecated, don't see mentions of it in the backend code
   */
  report_dl?: string;
  buttonText?: string;
  endpoint?: string;
  type?: string;
}

const DownloadReport = ({
  url_path,
  filters,
  report_dl = "",
  buttonText,
  endpoint = "/api/data_report/",
  type = "xls",
}: Props) => {
  const { t } = useTranslation(["perf_reports"]);
  const defaultButtonText = t(($) => $.perf_reports["Download Report"]);
  const downloadingText = t(($) => $.perf_reports["Downloading..."]);
  const offlineMessage = t(
    ($) => $.perf_reports["Could not download the report. Check your connection and try again."],
  );
  const failureMessage = t(($) => $.perf_reports["Could not download the report. Please try again."]);
  const resolvedButtonText = buttonText ?? defaultButtonText;
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = async () => {
    let url = URL_PREFIX + `${endpoint}${url_path}`;

    const params = JSON.parse(JSON.stringify(filters)) as Record<string, unknown>;
    params["xls"] = "true";
    params["report_dl"] = report_dl;

    let queryString = "";
    for (const [key, value] of Object.entries(params)) {
      if (value === null) {
        continue;
      }
      if (queryString.length === 0) {
        // oxlint-disable-next-line @typescript-eslint/no-base-to-string
        queryString += `${key}=${value?.toString()}`;
      } else {
        // oxlint-disable-next-line @typescript-eslint/no-base-to-string
        queryString += `&${key}=${value?.toString()}`;
      }
    }
    url = `${url}?${queryString}`;
    setIsLoading(true);

    try {
      if (type === "xls") {
        const response = await axios({
          method: "GET",
          url: url,
          responseType: "blob",
        });

        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
        });
        FileSaver.saveAs(blob, "report.xlsx");
        return;
      }
      const response = await axios({
        method: "GET",
        url: url,
      });

      // For CSV files
      const blob = new Blob([response.data], {
        type: "text/csv",
      });

      FileSaver.saveAs(blob, "report.csv");
    } catch (error) {
      errorToast({ message: isNetworkError(error) ? offlineMessage : failureMessage, error });
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button isPending={isLoading} onPress={handleExport} marginTop="1rem">
      <FaRegFileExcel /> {!isLoading ? resolvedButtonText : downloadingText}
    </Button>
  );
};

export default DownloadReport;

export const DownloadV4Report = ({
  downloadMutation,
  children,
  fileName = "report.xlsx",
}: {
  downloadMutation: UseMutationResult<Blob, unknown, void>;
  children?: React.ReactNode;
  fileName?: string;
}) => {
  const { t } = useTranslation(["perf_reports"]);
  const defaultLabel = t(($) => $.perf_reports["Download Report"]);
  const downloadingText = t(($) => $.perf_reports["Downloading..."]);
  return (
    <Button
      isPending={downloadMutation.isPending}
      onPress={() =>
        downloadMutation.mutate(undefined, {
          onSuccess: (data) => {
            FileSaver.saveAs(data, fileName);
          },
        })
      }
    >
      <FaRegFileExcel /> {!downloadMutation.isPending ? (children ?? defaultLabel) : downloadingText}
    </Button>
  );
};
